import React, { useEffect } from "react";

const slugify = (text) =>
	String(text)
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-|-$/g, "");

const sectionId = (props) => props.id || slugify(props.heading);

/** Shared chrome for the public legal pages (#157): privacy, terms, deletion. */
const LegalPage = ({ title, updated, intro, toc = false, children }) => {
	useEffect(() => {
		// A deep link to #section must win over the reset, or a pasted anchor URL
		// lands at the top of the page.
		const target = window.location.hash
			? document.getElementById(window.location.hash.slice(1))
			: null;
		if (target) target.scrollIntoView();
		else window.scrollTo(0, 0);
	}, []);

	// The privacy/terms pages run to 14 and 24 numbered sections, so they get a
	// jump list built from whichever children are Sections.
	const headings = toc
		? React.Children.toArray(children)
				.filter((child) => React.isValidElement(child) && child.type === Section)
				.map((child) => ({
					id: sectionId(child.props),
					heading: child.props.heading,
				}))
		: [];

	return (
		<div className="bg-gray-50">
			<div className="flex justify-center py-10 lg:py-16 bg-brand-100 w-full">
				<div className="flex mx-auto w-full max-w-screen-2xl px-3 sm:px-10">
					<div className="w-full flex justify-center flex-col">
						<h1 className="text-xl md:text-3xl lg:text-4xl font-bold text-center text-black">
							{title}
						</h1>
						{updated && (
							<p className="mt-3 text-center text-sm opacity-70">
								Last updated: {updated}
							</p>
						)}
					</div>
				</div>
			</div>
			<div className="bg-white">
				<div className="max-w-4xl mx-auto lg:py-16 py-10 px-4 sm:px-10 text-base opacity-90 leading-7">
					{intro && <div className="mb-10">{intro}</div>}
					{headings.length > 0 && (
						<nav
							aria-label="Contents"
							className="mb-10 rounded-lg border border-gray-200 bg-gray-50 p-5 sm:p-6"
						>
							<h2 className="text-base font-semibold text-black mb-3">
								Contents
							</h2>
							<ol className="grid gap-x-8 gap-y-1 sm:grid-cols-2 list-none pl-0">
								{headings.map(({ id, heading }) => (
									<li key={id}>
										<a
											href={`#${id}`}
											className="text-brand-600 hover:text-brand-700 hover:underline"
										>
											{heading}
										</a>
									</li>
								))}
							</ol>
						</nav>
					)}
					{children}
				</div>
			</div>
		</div>
	);
};

export const Section = ({ heading, id, children }) => (
	<section id={id || slugify(heading)} className="mb-8 scroll-mt-24">
		<h2 className="text-lg lg:text-2xl font-semibold mb-3 text-black">{heading}</h2>
		{children}
	</section>
);

export const SubSection = ({ heading, children }) => (
	<div className="mb-5">
		<h3 className="text-base lg:text-lg font-semibold mb-2 text-black">{heading}</h3>
		{children}
	</div>
);

export const Bullets = ({ items }) => (
	<ul className="list-disc pl-6 space-y-2">
		{items.map((item, index) => (
			<li key={index}>{item}</li>
		))}
	</ul>
);

/** Highlighted aside — used for statutory rights that override our own terms. */
export const Note = ({ children }) => (
	<div className="my-4 border-l-4 border-brand-500 bg-brand-50 px-4 py-3 rounded-r">
		{children}
	</div>
);

export default LegalPage;
